import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Input,
  Spinner,
} from 'reactstrap';
import { toast } from 'react-toastify';
import api from '~/services/api';

const TwoFactorActionModal = ({ isOpen, toggle, onSuccess }) => {
  const [step, setStep] = useState('loading'); // loading | code | verifying
  const [challengeId, setChallengeId] = useState(null);
  const [maskedEmail, setMaskedEmail] = useState('');
  const [codeLength, setCodeLength] = useState(6);
  const [inputs, setInputs] = useState([]);
  const [error, setError] = useState(null);
  const [resendLoading, setResendLoading] = useState(false);
  const inputRefs = useRef([]);

  const reset = useCallback(() => {
    setStep('loading');
    setChallengeId(null);
    setMaskedEmail('');
    setInputs([]);
    setError(null);
    setResendLoading(false);
  }, []);

  const requestChallenge = useCallback(async () => {
    setStep('loading');
    setError(null);

    try {
      const { data } = await api.post('auth/2fa/action-challenge');
      const len = Number(data.code_length) || 6;

      setChallengeId(data.challenge_id);
      setMaskedEmail(data.masked_email);
      setCodeLength(len);
      setInputs(Array(len).fill(''));
      setStep('code');

      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        'Não foi possível enviar o código de verificação.';
      toast.error(msg);
      toggle();
    }
  }, [toggle]);

  useEffect(() => {
    if (isOpen) {
      reset();
      requestChallenge();
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const fillFrom = (startIndex, text) => {
    const digits = (text || '').replace(/\D/g, '');
    if (!digits) return;

    const newInputs = [...inputs];
    for (let i = 0; i < codeLength - startIndex; i += 1) {
      newInputs[startIndex + i] = digits[i] || newInputs[startIndex + i];
    }

    setInputs(newInputs);

    const lastFilled = Math.min(startIndex + digits.length - 1, codeLength - 1);
    setTimeout(() => inputRefs.current[lastFilled]?.focus(), 0);
  };

  const handleInputChange = (index, value) => {
    const digits = (value || '').replace(/\D/g, '');

    if (!digits) {
      const newInputs = [...inputs];
      newInputs[index] = '';
      setInputs(newInputs);
      return;
    }

    if (digits.length === 1) {
      const newInputs = [...inputs];
      newInputs[index] = digits;
      setInputs(newInputs);

      if (index < codeLength - 1) {
        inputRefs.current[index + 1]?.focus();
      }
      return;
    }

    fillFrom(index, digits);
  };

  const handleVerify = async () => {
    const code = inputs.join('');
    if (code.length !== codeLength || !challengeId) return;

    setStep('verifying');
    setError(null);

    try {
      const { data } = await api.post('auth/2fa/action-verify', {
        challenge_id: challengeId,
        code,
      });

      reset();
      onSuccess(data.action_token);
    } catch (err) {
      const msg =
        err?.response?.data?.message || 'Código inválido. Tente novamente.';
      setError(msg);
      setStep('code');
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !inputs[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'Enter') {
      handleVerify();
    }
  };

  const handleResend = async () => {
    if (!challengeId) return;

    setResendLoading(true);
    setError(null);

    try {
      await api.post('auth/2fa/action-resend', {
        challenge_id: challengeId,
      });
      toast.success('Código reenviado.');
    } catch (err) {
      const msg =
        err?.response?.data?.message || 'Não foi possível reenviar o código.';
      setError(msg);
    } finally {
      setResendLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    toggle();
  };

  const code = inputs.join('');

  return (
    <Modal isOpen={isOpen} toggle={handleClose}>
      <ModalHeader toggle={handleClose}>Verificação em 2 etapas</ModalHeader>
      <ModalBody>
        {step === 'loading' && (
          <div className="text-center py-4">
            <Spinner color="primary" />
            <p className="mt-3 text-muted">Enviando código de verificação...</p>
          </div>
        )}

        {(step === 'code' || step === 'verifying') && (
          <>
            <p>
              Para sua segurança, enviamos um código de {codeLength} dígitos
              para o e-mail <b>{maskedEmail || 'cadastrado'}</b>.
            </p>

            <Row className="justify-content-center mb-3">
              {/* eslint-disable react/no-array-index-key */}
              {inputs.map((value, index) => (
                <Col xs={2} key={`2fa-input-${index}`}>
                  <Input
                    type="text"
                    maxLength={1}
                    className="text-center font-weight-bold font-size-24"
                    value={value}
                    onChange={e => handleInputChange(index, e.target.value)}
                    onPaste={e => {
                      e.preventDefault();
                      fillFrom(index, e.clipboardData.getData('text'));
                    }}
                    onKeyDown={e => handleKeyDown(e, index)}
                    innerRef={ref => {
                      inputRefs.current[index] = ref;
                    }}
                    disabled={step === 'verifying'}
                  />
                </Col>
              ))}
              {/* eslint-enable react/no-array-index-key */}
            </Row>

            {error && <p className="text-danger text-center">{error}</p>}

            <div className="text-center">
              <Button
                color="link"
                disabled={resendLoading || step === 'verifying'}
                onClick={handleResend}
              >
                {resendLoading ? 'Reenviando...' : 'Reenviar código'}
              </Button>
            </div>
          </>
        )}
      </ModalBody>

      {(step === 'code' || step === 'verifying') && (
        <ModalFooter>
          <Button
            color="secondary"
            onClick={handleClose}
            disabled={step === 'verifying'}
          >
            Cancelar
          </Button>
          <Button
            color="primary"
            onClick={handleVerify}
            disabled={code.length !== codeLength || step === 'verifying'}
          >
            {step === 'verifying' ? 'Validando...' : 'Confirmar'}
          </Button>
        </ModalFooter>
      )}
    </Modal>
  );
};

export default TwoFactorActionModal;
